import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { YearProvider } from './contexts/YearContext'
import { VehicleFilterProvider } from './contexts/VehicleFilterContext'
import ProtectedRoute from './components/ProtectedRoute'
import Layout from './components/Layout'
import { PublicLanguageSwitcher } from './components/LanguageSwitcher'
import Login from './pages/Login'
import Register from './pages/Register'
import ChangePassword from './pages/ChangePassword'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Dashboard from './pages/Dashboard'
import BrandAnalytics from './pages/BrandAnalytics'
import ModelVariantAnalytics from './pages/ModelVariantAnalytics'
import MasterModelAnalytics from './pages/MasterModelAnalytics'
import LeasingAnalytics from './pages/LeasingAnalytics'
import LoanAnalytics from './pages/LoanAnalytics'
import BruktbilfinansieringPage from './pages/BruktbilfinansieringPage'
import DealerDetailsPage from './pages/bruktbilfinansiering/DealerDetailsPage'
import KlasserDetailsPage from './pages/bruktbilfinansiering/KlasserDetailsPage'
import CompanyAnalytics from './pages/CompanyAnalytics'
import LeasingCampaigns from './pages/LeasingCampaigns'
import GeographicAnalytics from './pages/GeographicAnalytics'
import OrganizationAnalytics from './pages/OrganizationAnalytics'
import OrganizationDetails from './pages/OrganizationDetails'
import BrandYoYComparison from './pages/BrandYoYComparison'
import AgeAnalytics from './pages/AgeAnalytics'
import AgeAnalyticsBrandDetail from './pages/AgeAnalyticsBrandDetail'
import AgeAnalyticsMasterModelDetail from './pages/AgeAnalyticsMasterModelDetail'
import Teknisk from './pages/Teknisk/Teknisk'
import VarebilAnalytics from './pages/VarebilAnalytics'
import SegmentAnalytics from './pages/SegmentAnalytics'
import TotalStatistics from './pages/TotalStatistics'
import AndreKjoretoy from './pages/AndreKjoretoy'
import UserProfile from './pages/UserProfile'
import Announcements from './pages/Announcements'
import Feedback from './pages/Feedback'
import TermsAndConditions from './pages/TermsAndConditions'
import SubscriptionSignup from './pages/SubscriptionSignup'
import SubscriptionTerms from './pages/SubscriptionTerms'
import About from './pages/About'
import Hendelsessenter from './pages/Hendelsessenter'
import BruktbilLayout from './pages/Bruktbil/BruktbilLayout'
import OversiktPage from './pages/Bruktbil/Oversikt'
import KlasserPage from './pages/Bruktbil/Klasser'
import SelgerePage from './pages/Bruktbil/Selgere'
import KjoperePage from './pages/Bruktbil/Kjopere'
import KjopereDetaljerPage from './pages/Bruktbil/Kjopere/KjopereDetaljerPage'
import SelgereDetaljerPage from './pages/Bruktbil/Selgere/SelgereDetaljerPage'
import KlasseDetaljerPage from './pages/Bruktbil/Klasser/KlasseDetaljerPage'
import MerkeDetaljerPage from './pages/Bruktbil/MerkeModell/MerkeDetaljerPage'
import MerkeModellPage from './pages/Bruktbil/MerkeModell'
import AldersprofilPage from './pages/Bruktbil/Aldersprofil'
import PriserPage from './pages/Bruktbil/Priser'
import StaatidPage from './pages/Bruktbil/Staatid'
import LeadsPage from './pages/Leads/LeadsPage'
import BedriftDetail from './pages/Leads/BedriftDetail'
import NaceDetailPage from './pages/Leads/NaceDetailPage'
import OppslagLayout from './pages/Oppslag/OppslagLayout'
import TekniskTab from './pages/Oppslag/TekniskTab'
import KjoretoyTab from './pages/Oppslag/KjoretoyTab'
import PersonTab from './pages/Oppslag/PersonTab'
import HeftelserTab from './pages/Oppslag/HeftelserTab'
import KampanjerLayout from './pages/Kampanjer/KampanjerLayout'
import LoanCampaignsTab from './pages/Kampanjer/LoanCampaignsTab'
import {
  ROLE_USEDCAR,
  ROLE_USEDCAR_ADVANCED,
  ROLE_BRUKTBILFINANS,
  ROLE_LEADS,
  ROLE_OPPSLAG_BASIC,
} from './utils/roles'

/** Bruktbil dealer-level views need the advanced role; no admin bypass (#673). */
const kunAvansert = (el: ReactNode) => (
  <ProtectedRoute requiredRoles={[ROLE_USEDCAR_ADVANCED]} adminBypass={false}>{el}</ProtectedRoute>
)

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <YearProvider>
            <VehicleFilterProvider>
              <PublicLanguageSwitcher />
              <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/subscribe" element={<SubscriptionSignup />} />
            <Route path="/subscription-terms" element={<SubscriptionTerms />} />
            
            {/* Protected routes with Layout */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  <Navigate to="/dashboard" replace />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/change-password" element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout><Dashboard /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/brands" element={
              <ProtectedRoute>
                <Layout><BrandAnalytics /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/model-variants" element={
              <ProtectedRoute>
                <Layout><ModelVariantAnalytics /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/master-models" element={
              <ProtectedRoute>
                <Layout><MasterModelAnalytics /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/leasing" element={
              <ProtectedRoute>
                <Layout><LeasingAnalytics /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/loans" element={
              <ProtectedRoute>
                <Layout><LoanAnalytics /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/bruktbil-finansiering" element={
              <ProtectedRoute requiredRoles={[ROLE_BRUKTBILFINANS]}>
                <Layout><BruktbilfinansieringPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/bruktbil-finansiering/:bankOrgNr/dealer/:dealerOrgNr" element={
              <ProtectedRoute requiredRoles={[ROLE_BRUKTBILFINANS]}>
                <Layout><DealerDetailsPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/bruktbil-finansiering/:bankOrgNr/klasser/:classLabel" element={
              <ProtectedRoute requiredRoles={[ROLE_BRUKTBILFINANS]}>
                <Layout><KlasserDetailsPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/bruktbil-finansiering/:bankOrgNr" element={
              <ProtectedRoute requiredRoles={[ROLE_BRUKTBILFINANS]}>
                <Layout><BruktbilfinansieringPage /></Layout>
              </ProtectedRoute>
            } />

            {/* Bruktbil used-car analytics — nested routes under BruktbilLayout */}
            <Route path="/bruktbil" element={
              <ProtectedRoute requiredRoles={[ROLE_USEDCAR]} adminBypass={false}>
                <Layout><BruktbilLayout /></Layout>
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="oversikt" replace />} />
              <Route path="oversikt"     element={<OversiktPage />} />
              <Route path="klasser"      element={<KlasserPage />} />
              <Route path="klasser/:classLabel/detaljer" element={<KlasseDetaljerPage />} />
              <Route path="selgere"      element={kunAvansert(<SelgerePage />)} />
              <Route path="selgere/:orgNr" element={kunAvansert(<SelgereDetaljerPage />)} />
              <Route path="kjopere"      element={kunAvansert(<KjoperePage />)} />
              <Route path="kjopere/:orgNr" element={kunAvansert(<KjopereDetaljerPage />)} />
              <Route path="merke-modell" element={<MerkeModellPage />} />
              {/* Modell route first: ":merke/detaljer" would otherwise match
                  ":merke/:modell" with modell="detaljer" (#489 AC5/AC6). */}
              <Route path="merke-modell/:merke/:modell/detaljer" element={kunAvansert(<MerkeDetaljerPage />)} />
              <Route path="merke-modell/:merke/detaljer" element={kunAvansert(<MerkeDetaljerPage />)} />
              <Route path="aldersprofil" element={<AldersprofilPage />} />
              <Route path="priser"       element={kunAvansert(<PriserPage />)} />
              <Route path="staatid"      element={<StaatidPage />} />
            </Route>

            {/* Leads — gated by leads_advanced_user role */}
            <Route path="/leads" element={
              <ProtectedRoute requiredRoles={[ROLE_LEADS]}>
                <Layout><LeadsPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/leads/bedrift/:orgNr" element={
              <ProtectedRoute requiredRoles={[ROLE_LEADS]}>
                <Layout><BedriftDetail /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/leads/nace/:naceKode" element={
              <ProtectedRoute requiredRoles={[ROLE_LEADS]}>
                <Layout><NaceDetailPage /></Layout>
              </ProtectedRoute>
            } />

            {/* Oppslag — one page, four tabs. Only basic opens the page;
                premium alone is an API entitlement with no visual access.
                The three premium tabs are hidden without oppslag_premium_user
                and the backend rejects them independently. */}
            <Route path="/oppslag" element={
              <ProtectedRoute requiredRoles={[ROLE_OPPSLAG_BASIC]}>
                <Layout><OppslagLayout /></Layout>
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="teknisk" replace />} />
              <Route path="teknisk"  element={<TekniskTab />} />
              <Route path="kjoretoy" element={<KjoretoyTab />} />
              <Route path="person"   element={<PersonTab />} />
              <Route path="heftelser" element={<HeftelserTab />} />
            </Route>

            <Route path="/companies" element={
              <ProtectedRoute>
                <Layout><CompanyAnalytics /></Layout>
              </ProtectedRoute>
            } />
            
            {/* Kampanjer — one page, two tabs (#583). The leasing grid is the
                unchanged LeasingCampaigns page, now mounted as a tab. */}
            <Route path="/kampanjer" element={
              <ProtectedRoute>
                <Layout><KampanjerLayout /></Layout>
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="leasing" replace />} />
              <Route path="leasing" element={<LeasingCampaigns />} />
              <Route path="laan" element={<LoanCampaignsTab />} />
            </Route>

            {/* Bookmarks and shared links from before the move. */}
            <Route path="/leasingkampanjer" element={<Navigate to="/kampanjer/leasing" replace />} />

            <Route path="/geographic" element={
              <ProtectedRoute>
                <Layout><GeographicAnalytics /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/organizations" element={
              <ProtectedRoute>
                <Layout><OrganizationAnalytics /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/organizations/aktor/:orgnr" element={
              <ProtectedRoute>
                <Layout><OrganizationDetails /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/brand-comparison" element={
              <ProtectedRoute>
                <Layout><BrandYoYComparison /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/age-analytics" element={
              <ProtectedRoute>
                <Layout><AgeAnalytics /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/age-analytics/brand/:brandName" element={
              <ProtectedRoute>
                <Layout><AgeAnalyticsBrandDetail /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/age-analytics/model/:masterModelId" element={
              <ProtectedRoute>
                <Layout><AgeAnalyticsMasterModelDetail /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/teknisk" element={
              <ProtectedRoute>
                <Layout><Teknisk /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/varebil-analytics" element={
              <ProtectedRoute>
                <Layout><VarebilAnalytics /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/segment-analytics" element={
              <ProtectedRoute>
                <Layout><SegmentAnalytics /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/total-statistics" element={
              <ProtectedRoute>
                <Layout><TotalStatistics /></Layout>
              </ProtectedRoute>
            } />

            {/* Andre kjøretøy (#982): the class is the URL segment; an unknown one redirects */}
            <Route path="/andre-kjoretoy" element={<Navigate to="/andre-kjoretoy/lastebil" replace />} />
            <Route path="/andre-kjoretoy/:gruppe" element={
              <ProtectedRoute>
                <Layout><AndreKjoretoy /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout><UserProfile /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/announcements" element={
              <ProtectedRoute>
                <Layout><Announcements /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/feedback" element={
              <ProtectedRoute>
                <Layout><Feedback /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/hendelsessenter" element={
              <ProtectedRoute>
                <Layout><Hendelsessenter /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/about" element={
              <ProtectedRoute>
                <Layout><About /></Layout>
              </ProtectedRoute>
            } />
              </Routes>
            </VehicleFilterProvider>
          </YearProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default App
